import * as React from 'react';
import styled from '@emotion/styled';
import { config } from '../../../config';
import * as vars from '../../../consts/variables';
import { __ } from '../../../helpers/TranslationService';
import { validateDDD, validatePhone } from '../../../helpers/validation';
import { CountryCodeSelect } from './CountryCodeSelect';
import { Input } from './Input';

const styles: Record<string, React.CSSProperties> = {
    wrapper: {
        margin: '0 15px',
    },
    phoneSection: {
        display: 'flex',
    },
    countryCode: {
        flex: 1,
    },
    ddd: {
        flex: 1,
    },
    phone: {
        flex: 2,
    },
    inputWrapper: {
        marginRight: 0,
    },
    errorText: {
        fontSize: 14,
        color: vars.pink,
        marginTop: -15,
        marginBottom: 15,
    },
};

const StyledCountrySelect = styled(CountryCodeSelect)<{ isError?: boolean }>`
    .Select,
    .Select-control {
        border-radius: 8px;
        border-width: 2px;
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
        border-right-width: 0;
    }

    .Select-control {
        border-color: ${(props) =>
            props.isError ? props.theme.colors.watermelon[100] : props.theme.colors.iron[100]};
        height: 48px;
    }
`;

const StyledInput = styled(Input)`
    margin-left: 0 !important;
    & label > input {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
        margin: 0;
    }
`;

interface IProps {
    phone: string;
    countryCode: string;
    countryCodes: { code: string; label: string; country: string; countryCode: string }[];
    ddd: string;
    isSubmitted: boolean;
    onPhoneChange(event: React.ChangeEvent<any>): void;
    onDDDChange(event: React.ChangeEvent<any>): void;
    onCountryCodeChange(value: string): void;
    portalId: string;
    firstInvalidFormField: string;
    testSelectorName?: string;
    muiStyling?: boolean;
}

export class PhoneSelection extends React.Component<IProps, {}> {
    constructor(props) {
        super(props);
    }

    onCountryCodeChange = (selected: { value: string; label: string }) => {
        this.props.onCountryCodeChange(selected.value);
    };

    getOptionsForCountryCodeSelect = () =>
        this.props.countryCodes.map((item) => ({
            value: item.code,
            label: item.label,
            icon: item.countryCode ? `${config.images}/common/flags/${item.countryCode}.svg` : null,
        }));

    getMaskByCountry = (portalId, countryCode) => {
        if (portalId === 'dreamlines.com' && countryCode === '+1') {
            return [
                /[0-9]/,
                ' ',
                /[0-9]/,
                ' ',
                /[0-9]/,
                ' ',
                '-',
                ' ',
                /[0-9]/,
                ' ',
                /[0-9]/,
                ' ',
                /[0-9]/,
                ' ',
                '-',
                ' ',
                /[0-9]/,
                ' ',
                /[0-9]/,
                ' ',
                /[0-9]/,
                ' ',
                /[0-9]/,
            ];
        }
        return '';
    };

    render() {
        const {
            isSubmitted,
            phone,
            onPhoneChange,
            ddd,
            onDDDChange,
            countryCode,
            portalId,
            firstInvalidFormField,
        } = this.props;
        const options = this.getOptionsForCountryCodeSelect();
        const isCountryCodeError =
            isSubmitted &&
            (!Boolean(this.props.countryCode) || this.props.countryCode.length === 0);
        const countryCodeOptions = {
            onChange: this.onCountryCodeChange,
            options: options,
            value: options.find((item) => item.value === this.props.countryCode),
            //TODO: add translatons for
            placeholder: __('mobile.personalData.countryCode', 'dreamlines'),
            isError: isCountryCodeError,
            testSelectorName: 'countrySelect',
        };
        const isPhoneError = isSubmitted && !validatePhone(phone);
        const phoneProps = {
            placeholder: this.props.muiStyling
                ? null
                : __('mobile.personalData.phone', 'dreamlines'),
            value: phone,
            onChange: onPhoneChange,
            isError: isPhoneError,
            wrapperStyle: styles.inputWrapper,
            isFirstInvalidFormField: firstInvalidFormField === 'phone',
            type: 'tel',
            testSelectorName: 'phone',
            mask: this.getMaskByCountry(portalId, countryCode),
            maskPlaceholder: '_ _ _ - _ _ _ - _ _ _ _',
        };
        const isDDDError = isSubmitted && !validateDDD(ddd);
        const dddProps = {
            placeholder: __('personalData.DDD', 'dreamlines'),
            value: ddd,
            onChange: onDDDChange,
            isError: isDDDError,
            wrapperStyle: styles.inputWrapper,
            isFirstInvalidFormField: firstInvalidFormField === 'DDD',
            testSelectorName: 'ddd',
        };
        const isError = isCountryCodeError || isPhoneError;
        const isDDDShown =
            countryCode === vars.brasilCountryCode ||
            (countryCode === '' && portalId === 'dreamlines.com.br');
        return (
            <div style={styles.wrapper}>
                <div style={styles.phoneSection}>
                    <div style={styles.countryCode}>
                        {this.props.muiStyling ? (
                            <StyledCountrySelect
                                {...countryCodeOptions}
                                muiStyling
                                isError={isError}
                            />
                        ) : (
                            <CountryCodeSelect {...countryCodeOptions} />
                        )}
                    </div>
                    {isDDDShown && (
                        <div style={styles.ddd}>
                            <Input {...dddProps} />
                        </div>
                    )}
                    <div style={styles.phone}>
                        {this.props.muiStyling ? (
                            <StyledInput
                                muiStyling
                                testSelectorName={this.props.testSelectorName}
                                {...phoneProps}
                            />
                        ) : (
                            <Input {...phoneProps} testSelectorName={this.props.testSelectorName} />
                        )}
                    </div>
                </div>
                <div style={styles.errorText}>{isError && __('errors.phone', 'dreamlines')}</div>
            </div>
        );
    }
}
